import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';
import {
  loadLearningPaths as loadLearningPathsFromStorage,
  saveLearningPaths,
  addLearningPath,
  updateLearningPath,
  deleteLearningPath,
  publishLearningPath,
  resetToDefaults,
  exportToJson,
  importFromJson
} from '../utils/learningPathsStorage';


const LearningPathsManagement = () => {
  const { currentColors } = useAdminTheme();
  const [learningPaths, setLearningPaths] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [newPath, setNewPath] = useState({
    title: '',
    description: '',
    level: 'beginner',
    duration: '',
    modules_count: 1,
    subscription_required: 'basic',
    author_name: 'Admin User',
    author_role: 'Content Manager',
    status: 'draft',
    useAI: false,
    aiPrompt: ''
  });

  const [editingPath, setEditingPath] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadLearningPaths();
  }, []);

  const loadLearningPaths = async () => {
    try {
      setLoading(true);
      console.log('=== LearningPathsManagement: Loading learning paths from localStorage ===');

      const data = await loadLearningPathsFromStorage();
      console.log('=== LearningPathsManagement: Data received from storage:', data);
      console.log('=== LearningPathsManagement: Number of paths loaded:', data?.length || 0);

      if (data && data.length > 0) {
        console.log('=== LearningPathsManagement: Path titles:', data.map(p => p.title));
        console.log('=== LearningPathsManagement: Path statuses:', data.map(p => ({ title: p.title, status: p.status })));
      }

      if (!data || data.length === 0) {
        console.log('No learning paths found');
        setLearningPaths([]);
      } else {
        console.log(`Loaded ${data.length} learning paths`);
        setLearningPaths(data);
      }

    } catch (error) {
      console.error('Failed to load learning paths:', error);
      console.error('Error details:', error);
      alert(`Failed to load learning paths: ${error.message}`);
      setLearningPaths([]);
    } finally {
      setLoading(false);
    }
  };

  const saveLearningPath = async () => {
    try {
      const updatedPaths = addLearningPath(newPath);
      setLearningPaths(updatedPaths);

      setNewPath({
        title: '',
        description: '',
        level: 'beginner',
        duration: '',
        modules_count: 1,
        subscription_required: 'basic',
        author_name: 'Admin User',
        author_role: 'Content Manager',
        status: 'draft',
        useAI: false,
        aiPrompt: ''
      });
      setShowCreateModal(false);
      alert('Learning path created successfully!');
    } catch (error) {
      console.error('Error saving learning path:', error);
      alert('Error saving learning path: ' + error.message);
    }
  };

  const deleteLearningPath = async (pathId) => {
    if (window.confirm('Are you sure you want to delete this learning path?')) {
      try {
        const updatedPaths = deleteLearningPath(pathId);
        setLearningPaths(updatedPaths);
        alert('Learning path deleted successfully!');
      } catch (error) {
        console.error('Error deleting learning path:', error);
        alert('Error deleting learning path: ' + error.message);
      }
    }
  };

  const editLearningPath = (path) => {
    setEditingPath({
      ...path,
      modules_count: path.modules_count || 1
    });
    setShowEditModal(true);
  };

  const updateLearningPath = async () => {
    try {
      const updatedPaths = updateLearningPath(editingPath);
      setLearningPaths(updatedPaths);
      setEditingPath(null);
      setShowEditModal(false);
      alert('Learning path updated successfully!');
    } catch (error) {
      console.error('Error updating learning path:', error);
      alert('Error updating learning path: ' + error.message);
    }
  };

  const generateAIContent = async (pathId) => {
    try {
      const path = learningPaths.find(p => p.id === pathId);
      if (!path) return;

      const confirmed = window.confirm(`Generate AI content for "${path.title}"? This will enhance the description and add structured modules.`);
      if (!confirmed) return;

      // For demo, we'll use a simplified version
      const aiEnhancement = {
        enhancedDescription: `${path.description} This comprehensive program includes hands-on exercises, real-world case studies, and interactive assessments designed to accelerate your expertise in ${path.title.toLowerCase()}.`,
        objectives: [
          `Master the fundamentals of ${path.title.toLowerCase()}`,
          'Apply best practices in real-world scenarios',
          'Develop strategic thinking and problem-solving skills',
          'Build confidence in implementation and execution'
        ],
        modules: Array.from({length: path.modules_count}, (_, i) => ({
          id: `m-${pathId.split('-')[1]}-${i + 1}`,
          title: `Module ${i + 1}: Core Concepts ${i + 1}`,
          description: `In-depth exploration of key concepts and practical applications.`,
          videoUrl: ''
        }))
      };

      // Update the learning path with AI-generated content
      const updatedPath = {
        ...path,
        description: aiEnhancement.enhancedDescription,
        modules: aiEnhancement.modules
      };

      const updatedPaths = updateLearningPath(updatedPath);
      setLearningPaths(updatedPaths);
      alert('AI content generated successfully!');
    } catch (error) {
      console.error('Error generating AI content:', error);
      alert('Error generating AI content: ' + error.message);
    }
  };

  const publishLearningPath = async (pathId) => {
    try {
      const updatedPaths = publishLearningPath(pathId);
      setLearningPaths(updatedPaths);
      alert('Learning path published successfully!');
    } catch (error) {
      console.error('Error publishing learning path:', error);
      alert('Error publishing learning path: ' + error.message);
    }
  };

  const levels = ['beginner', 'intermediate', 'advanced'];
  const subscriptionLevels = ['free', 'basic', 'premium', 'enterprise'];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin" style={{ color: currentColors.primary }} />
        <span className="ml-2" style={{ color: currentColors.text }}>Loading learning paths...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Learning Paths Management
          </h2>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            Create and manage structured learning experiences for users
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              localStorage.removeItem('bootminds_learning_paths');
              loadLearningPaths();
            }}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 border"
            style={{ borderColor: currentColors.success, color: currentColors.success }}
            title="Force refresh from JSON file"
          >
            <Icon name="RefreshCw" size={16} className="inline mr-2" />
            Force Refresh
          </button>
          <button
            onClick={() => exportToJson()}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 border"
            style={{ borderColor: currentColors.border, color: currentColors.text }}
            title="Export current data to JSON file"
          >
            <Icon name="Download" size={16} className="inline mr-2" />
            Export
          </button>
          <button
            onClick={async () => {
              if (window.confirm('Reset all learning paths to default data? This will overwrite current changes.')) {
                const defaults = await resetToDefaults();
                setLearningPaths(defaults);
                alert('Reset to defaults completed!');
              }
            }}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90 border"
            style={{ borderColor: currentColors.warning, color: currentColors.warning }}
            title="Reset to JSON defaults"
          >
            <Icon name="RotateCcw" size={16} className="inline mr-2" />
            Reset
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
            style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
          >
            <Icon name="Plus" size={16} className="inline mr-2" />
            Create Learning Path
          </button>
        </div>
      </div>

      {/* Learning Paths List */}
      <div 
        className="rounded-xl shadow-sm border overflow-hidden"
        style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: currentColors.background }}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Learning Path
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Level
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Modules
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {learningPaths.map((path) => (
                <tr key={path.id} className="border-t" style={{ borderColor: currentColors.border }}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium" style={{ color: currentColors.text }}>
                        {path.title}
                      </p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>
                        {path.description?.substring(0, 80)}...
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${currentColors.info}20`,
                            color: currentColors.info
                          }}
                        >
                          {path.enrolled_count || 0} enrolled
                        </span>
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${currentColors.warning}20`,
                            color: currentColors.warning
                          }}
                        >
                          ★ {path.rating || 0}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                      style={{
                        backgroundColor: path.level === 'beginner' ? `${currentColors.success}20` :
                                        path.level === 'intermediate' ? `${currentColors.warning}20` :
                                        `${currentColors.error}20`,
                        color: path.level === 'beginner' ? currentColors.success :
                               path.level === 'intermediate' ? currentColors.warning :
                               currentColors.error
                      }}
                    >
                      {path.level}
                    </span>
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {path.duration}
                  </td>
                  <td className="px-6 py-4" style={{ color: currentColors.textSecondary }}>
                    {path.modules_count} modules
                  </td>
                  <td className="px-6 py-4">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: path.status === 'published' ? `${currentColors.success}20` : `${currentColors.warning}20`,
                        color: path.status === 'published' ? currentColors.success : currentColors.warning
                      }}
                    >
                      {path.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedPath(path);
                          setShowDetailModal(true);
                        }}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="View Details"
                      >
                        <Icon name="Eye" size={16} style={{ color: currentColors.info }} />
                      </button>
                      
                      <button 
                        onClick={() => editLearningPath(path)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Edit"
                      >
                        <Icon name="Edit" size={16} style={{ color: currentColors.primary }} />
                      </button>

                      {path.status === 'draft' && (
                        <button 
                          onClick={() => publishLearningPath(path.id)}
                          className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                          title="Publish Learning Path"
                        >
                          <Icon name="CheckCircle" size={16} style={{ color: currentColors.success }} />
                        </button>
                      )}
                      
                      <button 
                        onClick={() => generateAIContent(path.id)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Generate AI Content"
                      >
                        <Icon name="Sparkles" size={16} style={{ color: currentColors.warning }} />
                      </button>
                      
                      <button 
                        onClick={() => deleteLearningPath(path.id)}
                        className="p-1 rounded hover:bg-opacity-10 hover:bg-muted-foreground"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} style={{ color: currentColors.error }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Learning Path Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Create New Learning Path
              </h3>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Learning Path Title *
                </label>
                <input
                  type="text"
                  value={newPath.title}
                  onChange={(e) => setNewPath(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: currentColors.background, 
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Enter learning path title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Description *
                </label>
                <textarea
                  value={newPath.description}
                  onChange={(e) => setNewPath(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: currentColors.background, 
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                  placeholder="Describe what students will learn..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Difficulty Level *
                  </label>
                  <select
                    value={newPath.level}
                    onChange={(e) => setNewPath(prev => ({ ...prev, level: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: currentColors.background, 
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    {levels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Duration *
                  </label>
                  <input
                    type="text"
                    value={newPath.duration}
                    onChange={(e) => setNewPath(prev => ({ ...prev, duration: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: currentColors.background, 
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                    placeholder="e.g., 4 weeks, 2 months..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Number of Modules *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newPath.modules_count}
                    onChange={(e) => setNewPath(prev => ({ ...prev, modules_count: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: currentColors.background, 
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Subscription Required *
                  </label>
                  <select
                    value={newPath.subscription_required}
                    onChange={(e) => setNewPath(prev => ({ ...prev, subscription_required: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: currentColors.background, 
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    {subscriptionLevels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{ backgroundColor: currentColors.border, color: currentColors.text }}
              >
                Cancel
              </button>
              <button
                onClick={saveLearningPath}
                disabled={!newPath.title || !newPath.description}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50"
                style={{ backgroundColor: currentColors.primary, color: '#ffffff' }}
              >
                Save Learning Path
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Path Detail Modal */}
      {showDetailModal && selectedPath && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Learning Path Details
              </h3>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-muted-foreground"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-2xl font-bold mb-2" style={{ color: currentColors.text }}>
                  {selectedPath.title}
                </h4>
                <p className="text-lg mb-4" style={{ color: currentColors.textSecondary }}>
                  {selectedPath.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}>
                    {selectedPath.level}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.info}20`, color: currentColors.info }}>
                    {selectedPath.duration}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.success}20`, color: currentColors.success }}>
                    {selectedPath.modules_count} modules
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: `${currentColors.warning}20`, color: currentColors.warning }}>
                    {selectedPath.subscription_required} required
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedPath.enrolled_count || 0}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Enrolled
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedPath.rating || 0}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Rating
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedPath.completion_rate || 0}%
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Completion
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                      {selectedPath.modules_count}
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      Modules
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className="p-4 rounded-lg border"
                style={{ backgroundColor: currentColors.background, borderColor: currentColors.border }}
              >
                <h5 className="font-semibold mb-2" style={{ color: currentColors.text }}>
                  Author Information
                </h5>
                <p style={{ color: currentColors.textSecondary }}>
                  <strong>Name:</strong> {selectedPath.author_name || 'Unknown Author'}
                </p>
                <p style={{ color: currentColors.textSecondary }}>
                  <strong>Role:</strong> {selectedPath.author_role || 'Content Creator'}
                </p>
                <p style={{ color: currentColors.textSecondary }}>
                  <strong>Created:</strong> {new Date(selectedPath.created_at).toLocaleDateString()}
                </p>
                {selectedPath.published_at && (
                  <p style={{ color: currentColors.textSecondary }}>
                    <strong>Published:</strong> {new Date(selectedPath.published_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Learning Path Modal */}
      {showEditModal && editingPath && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-card rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.surface }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: currentColors.text }}>
                Edit Learning Path
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-lg hover:bg-muted"
              >
                <Icon name="X" size={20} style={{ color: currentColors.text }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Title
                </label>
                <input
                  type="text"
                  value={editingPath.title}
                  onChange={(e) => setEditingPath({...editingPath, title: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                  Description
                </label>
                <textarea
                  value={editingPath.description}
                  onChange={(e) => setEditingPath({...editingPath, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg"
                  style={{
                    backgroundColor: currentColors.background,
                    borderColor: currentColors.border,
                    color: currentColors.text
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Level
                  </label>
                  <select
                    value={editingPath.level}
                    onChange={(e) => setEditingPath({...editingPath, level: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Duration
                  </label>
                  <input
                    type="text"
                    value={editingPath.duration}
                    onChange={(e) => setEditingPath({...editingPath, duration: e.target.value})}
                    placeholder="e.g., 6-8 weeks"
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Modules Count
                  </label>
                  <input
                    type="number"
                    value={editingPath.modules_count}
                    onChange={(e) => setEditingPath({...editingPath, modules_count: parseInt(e.target.value)})}
                    min="1"
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentColors.text }}>
                    Subscription Required
                  </label>
                  <select
                    value={editingPath.subscription_required}
                    onChange={(e) => setEditingPath({...editingPath, subscription_required: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    style={{
                      backgroundColor: currentColors.background,
                      borderColor: currentColors.border,
                      color: currentColors.text
                    }}
                  >
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-lg font-medium"
                style={{
                  backgroundColor: currentColors.border,
                  color: currentColors.text
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateLearningPath}
                className="px-4 py-2 rounded-lg font-medium text-white"
                style={{ backgroundColor: currentColors.primary }}
              >
                Update Learning Path
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningPathsManagement;
